import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  ChevronRight,
  Download,
  Loader2,
  FileText,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  Printer,
  FileDown,
} from "lucide-react";

import { useLanguage } from "@/lib/LanguageContext";
import {
  CATEGORY_BY_SLUG,
  formatPeriod,
  buildPdfUrl,
} from "@/lib/laporan";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { exportReportToPdf } from "@/lib/pdf";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Renderers per category ----------
const PMKPView = ({ entry, t }) => {
  const inds = entry?.data?.indicators || [];
  const inc = entry?.data?.insiden || {};
  const chartData = inds.map((i) => ({
    name: i.name.length > 22 ? i.name.slice(0, 22) + "…" : i.name,
    Target: i.target,
    Realisasi: i.actual,
  }));
  const incidentData = [
    { name: t.laporan.insiden_ikp, value: inc.ikp || 0, fill: "#f59e0b" },
    { name: t.laporan.insiden_knc, value: inc.knc || 0, fill: "#3b82f6" },
    { name: t.laporan.insiden_ktd, value: inc.ktd || 0, fill: "#ef4444" },
    { name: t.laporan.insiden_sentinel, value: inc.sentinel || 0, fill: "#7c3aed" },
  ];
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 p-6" data-testid="pmkp-chart">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
          {t.laporan.indicator} — {t.laporan.target} vs {t.laporan.actual}
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" fontSize={10} interval={0} angle={-15} textAnchor="end" height={70} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Target" fill="#94a3b8" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Realisasi" fill="#1d4ed8" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100">
                <th className="py-2 pr-4">{t.laporan.indicator}</th>
                <th className="py-2 pr-4">{t.laporan.target}</th>
                <th className="py-2 pr-4">{t.laporan.actual}</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {inds.map((i, k) => {
                const invert = /IDO|IKP|infeksi/i.test(i.name);
                const met = invert ? i.actual <= i.target : i.actual >= i.target;
                return (
                  <tr key={k} className="border-b border-slate-50">
                    <td className="py-2 pr-4 text-slate-700">{i.name}</td>
                    <td className="py-2 pr-4 text-slate-600 tabular-nums">
                      {i.target}
                      {i.unit}
                    </td>
                    <td className="py-2 pr-4 font-semibold text-slate-900 tabular-nums">
                      {i.actual}
                      {i.unit}
                    </td>
                    <td className="py-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          met
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {met ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : (
                          <AlertTriangle className="h-3 w-3" />
                        )}
                        {met ? t.laporan.status_met : t.laporan.status_below}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 p-6">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
          {t.laporan.insiden_title}
        </h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={incidentData}
                dataKey="value"
                nameKey="name"
                innerRadius={45}
                outerRadius={80}
                paddingAngle={3}
              >
                {incidentData.map((d, i) => (
                  <Cell key={i} fill={d.fill} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 space-y-2">
          {incidentData.map((d) => (
            <div key={d.name} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />
                <span className="text-slate-600">{d.name}</span>
              </div>
              <span className="font-semibold tabular-nums text-slate-900">{d.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Top10View = ({ entry, t }) => {
  const items = entry?.data?.items || [];
  const chartData = items.map((i) => ({ name: i.name.length > 28 ? i.name.slice(0, 28) + "…" : i.name, count: i.count }));
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-100 p-6" data-testid="top10-chart">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
          {t.laporan.cases}
        </h3>
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" fontSize={11} />
              <YAxis type="category" dataKey="name" fontSize={11} width={200} />
              <Tooltip />
              <Bar dataKey="count" fill="#1d4ed8" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 p-6 overflow-x-auto">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
          Detail
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100">
              <th className="py-2 pr-3">#</th>
              <th className="py-2 pr-3">{t.laporan.icd}</th>
              <th className="py-2 pr-3">{t.laporan.disease}</th>
              <th className="py-2 text-right">{t.laporan.cases}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.rank} className="border-b border-slate-50">
                <td className="py-2 pr-3 font-semibold text-slate-500">{i.rank}</td>
                <td className="py-2 pr-3 text-slate-500 tabular-nums font-mono text-xs">{i.code}</td>
                <td className="py-2 pr-3 text-slate-800">{i.name}</td>
                <td className="py-2 text-right font-semibold text-slate-900 tabular-nums">{i.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const KunjunganView = ({ entry, all, t }) => {
  const d = entry?.data || {};
  const trend = [...(all || [])]
    .sort((a, b) => (a.year - b.year) || (a.month - b.month))
    .map((x) => ({
      periode: `${["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"][x.month]} ${String(x.year).slice(-2)}`,
      IGD: x.data?.igd || 0,
      RJ: x.data?.rawat_jalan || 0,
      RI: x.data?.rawat_inap || 0,
      MCU: x.data?.medical_check_up || 0,
    }));
  const cards = [
    { key: "igd", label: t.laporan.igd, value: d.igd || 0, color: "from-red-500 to-rose-600" },
    { key: "poli", label: t.laporan.poli, value: d.rawat_jalan || 0, color: "from-blue-600 to-cyan-600" },
    { key: "inap", label: t.laporan.inap, value: d.rawat_inap || 0, color: "from-emerald-500 to-teal-600" },
    { key: "mcu", label: t.laporan.mcu, value: d.medical_check_up || 0, color: "from-violet-500 to-fuchsia-600" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kunjungan-cards">
        {cards.map((c) => (
          <div key={c.key} className="bg-white rounded-2xl border border-slate-100 p-5 relative overflow-hidden">
            <div className={`absolute top-0 right-0 h-24 w-24 -mr-8 -mt-8 rounded-full bg-gradient-to-br ${c.color} opacity-10`} />
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{c.label}</div>
            <div className="font-heading text-2xl md:text-3xl font-bold text-slate-900 tabular-nums">{c.value.toLocaleString("id-ID")}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 p-6" data-testid="kunjungan-trend">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">
          Trend {t.laporan.total_visits}
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="periode" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="IGD" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="RJ" stroke="#1d4ed8" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="RI" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="MCU" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-right text-sm text-slate-500">
          {t.laporan.total_visits}:{" "}
          <span className="font-semibold text-slate-900 tabular-nums">
            {(d.total || 0).toLocaleString("id-ID")}
          </span>
        </div>
      </div>
    </div>
  );
};

// ---------- Main page ----------
const LaporanDetail = () => {
  const { category } = useParams();
  const { t, lang } = useLanguage();
  const cfg = CATEGORY_BY_SLUG[category];
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const contentRef = useRef(null);

  const handleDownloadPdf = async () => {
    if (!contentRef.current) {
      toast.error("Konten belum siap");
      return;
    }
    const periodLabel =
      (selected &&
        (periodOptions.find(
          (p) => p.value === `${selected.year}-${selected.month}`
        )?.label ||
          `${selected.month}/${selected.year}`)) ||
      "";
    setDownloadingPdf(true);
    try {
      await exportReportToPdf({
        node: contentRef.current,
        filename: `laporan-${category}${
          selected ? `-${selected.year}-${selected.month}` : ""
        }.pdf`,
        logoUrl: "/logo.jpg",
        headingLines: [
          "RS BHAYANGKARA TARAKAN",
          "Polda Kalimantan Utara",
          t.laporan[cfg.key_title],
          periodLabel ? `Periode: ${periodLabel}` : "",
        ],
      });
      toast.success(lang === "en" ? "PDF downloaded" : "PDF berhasil diunduh");
    } catch (e) {
      toast.error(lang === "en" ? "Failed to create PDF" : "Gagal membuat PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    if (!cfg) return;
    setLoading(true);
    axios
      .get(`${API}/laporan/${category}`)
      .then((r) => {
        setItems(r.data || []);
        setSelected((r.data && r.data[0]) || null);
      })
      .catch((e) => setError(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [category, cfg]);

  const periodOptions = useMemo(
    () =>
      items.map((x) => ({
        value: `${x.year}-${x.month}`,
        label: formatPeriod(x.year, x.month, lang),
        entry: x,
      })),
    [items, lang]
  );

  if (!cfg) return <Navigate to="/laporan" replace />;

  const pdfHref = buildPdfUrl(selected?.pdf_url);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 print-report" data-testid={`laporan-page-${category}`}>
      <div className="no-print"><Header /></div>
      <main className="flex-1 pt-28 md:pt-32 pb-16 print:pt-0">
        <div className="container-x">
          {/* Breadcrumb */}
          <div className="mb-4 flex items-center gap-2 text-xs text-slate-500 no-print">
            <Link to="/" className="hover:text-blue-800">{t.nav.home}</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/laporan" className="hover:text-blue-800">{t.nav.laporan}</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-blue-800 font-medium">{t.laporan[cfg.key_title]}</span>
          </div>

          {/* Print-only header */}
          <div className="hidden print:block mb-6 text-center border-b pb-4">
            <div className="text-lg font-bold">RS BHAYANGKARA TARAKAN</div>
            <div className="text-sm">Polda Kalimantan Utara</div>
            <div className="text-base font-semibold mt-2">
              {t.laporan[cfg.key_title]}
            </div>
            {selected && (
              <div className="text-sm mt-1">
                Periode:{" "}
                {periodOptions.find(
                  (p) => p.value === `${selected.year}-${selected.month}`
                )?.label || `${selected.month}/${selected.year}`}
              </div>
            )}
          </div>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 no-print">
            <div>
              <Link
                to="/laporan"
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-800 mb-3"
              >
                <ArrowLeft className="h-3 w-3" />
                {t.laporan.back_to_reports}
              </Link>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
                {t.laporan[cfg.key_title]}
              </h1>
              <p className="text-sm text-slate-500 mt-1">{t.laporan[cfg.key_desc]}</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                  {t.laporan.select_period}
                </label>
                <select
                  data-testid="laporan-period-select"
                  value={selected ? `${selected.year}-${selected.month}` : ""}
                  onChange={(e) => {
                    const opt = periodOptions.find((p) => p.value === e.target.value);
                    setSelected(opt?.entry || null);
                  }}
                  className="min-w-[180px] px-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                >
                  {periodOptions.length === 0 && <option value="">—</option>}
                  {periodOptions.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              {pdfHref ? (
                <a
                  href={pdfHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="laporan-download-pdf"
                  className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-md shadow-blue-900/20"
                >
                  <Download className="h-4 w-4" />
                  {t.laporan.download_pdf}
                </a>
              ) : (
                <span
                  data-testid="laporan-no-pdf"
                  className="inline-flex items-center gap-2 bg-slate-100 text-slate-500 text-sm font-medium px-5 py-3 rounded-xl"
                  title={t.laporan.no_pdf}
                >
                  <FileText className="h-4 w-4" />
                  {t.laporan.no_pdf}
                </span>
              )}
              <button
                onClick={() => window.print()}
                data-testid="laporan-print-btn"
                className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-md"
              >
                <Printer className="h-4 w-4" />
                Cetak
              </button>
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf || !selected}
                data-testid="laporan-download-generated-pdf"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-md disabled:opacity-60"
              >
                {downloadingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                {downloadingPdf
                  ? lang === "en"
                    ? "Preparing..."
                    : "Menyiapkan..."
                  : lang === "en"
                  ? "Download PDF"
                  : "Unduh PDF"}
              </button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="p-16 flex items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : error ? (
            <div className="p-8 rounded-3xl bg-red-50 text-red-700 border border-red-100">
              {String(error)}
            </div>
          ) : !selected ? (
            <div className="p-16 rounded-3xl bg-white border border-dashed border-slate-200 text-center text-slate-400">
              {t.laporan.empty}
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div ref={contentRef} className="bg-slate-50 pt-1">
                {category === "pmkp" && <PMKPView entry={selected} t={t} />}
                {(category === "rawat-jalan" || category === "rawat-inap") && (
                  <Top10View entry={selected} t={t} />
                )}
                {category === "kunjungan" && (
                  <KunjunganView entry={selected} all={items} t={t} />
                )}
              </div>
            </motion.div>
          )}

          {selected?.note && (
            <div className="mt-6 text-xs text-slate-400 italic">
              {selected.note}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default LaporanDetail;
